import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';

export interface TranslationResponse {
  language_code: string;
  translations: Record<string, string>;
}

export interface LanguageResponse {
  languages: string[];
}

export interface SingleTranslationResponse {
  key: string;
  language_code: string;
  text: string;
}

class TranslationService {
  private translations: Record<string, string> = {};
  private currentLanguage: string = 'en';
  private isLoaded: boolean = false;

  // Idiomas soportados con sus códigos y nombres nativos
  private readonly supportedLanguages = {
    'en': 'English',
    'es': 'Español', 
    'zh': '中文',
    'ru': 'Русский',
    'it': 'Italiano',
    'ht': 'Kreyòl Ayisyen',
    'ko': '한국어',
    'fr': 'Français',
    'tl': 'Tagalog',
    'pl': 'Polski',
    'ar': 'العربية',
    'yi': 'ייִדיש',
    'hi': 'हिन्दी',
    'ur': 'اردو',
    'el': 'Ελληνικά',
    'he': 'עברית',
    'pt': 'Português'
  };

  // Idiomas RTL (derecha a izquierda)
  private readonly rtlLanguages = ['ar', 'he', 'ur', 'yi'];

  constructor() {
    this.initializeLanguage();
  }

  /**
   * Detecta el idioma del navegador y lo mapea a nuestros códigos soportados
   */
  private detectBrowserLanguage(): string {
    const browserLang = navigator.language || (navigator as any).userLanguage;
    
    // Mapear códigos de idioma del navegador a nuestros códigos
    const langMap: Record<string, string> = {
      'en': 'en',
      'en-US': 'en',
      'en-GB': 'en',
      'es': 'es',
      'es-ES': 'es',
      'es-MX': 'es',
      'zh': 'zh',
      'zh-CN': 'zh',
      'zh-TW': 'zh',
      'zh-HK': 'zh',
      'ru': 'ru',
      'ru-RU': 'ru',
      'it': 'it',
      'it-IT': 'it',
      'ko': 'ko',
      'ko-KR': 'ko',
      'fr': 'fr',
      'fr-FR': 'fr',
      'fr-CA': 'fr',
      'pl': 'pl',
      'pl-PL': 'pl',
      'ar': 'ar',
      'ar-SA': 'ar',
      'ar-EG': 'ar',
      'hi': 'hi',
      'hi-IN': 'hi',
      'el': 'el',
      'el-GR': 'el',
      'he': 'he',
      'he-IL': 'he',
      'pt': 'pt',
      'pt-BR': 'pt',
      'pt-PT': 'pt'
    };

    // Obtener el código base del idioma (sin región)
    const baseLang = browserLang.split('-')[0];
    
    // Verificar si tenemos soporte directo
    if (langMap[browserLang]) {
      return langMap[browserLang];
    }
    
    // Verificar si tenemos soporte para el idioma base
    if (langMap[baseLang]) {
      return langMap[baseLang];
    }

    // Fallback a inglés
    return 'en';
  }

  /**
   * Obtiene el idioma actual
   */
  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  /**
   * Establece el idioma actual
   */
  setCurrentLanguage(languageCode: string): void {
    if (this.supportedLanguages[languageCode as keyof typeof this.supportedLanguages]) {
      this.currentLanguage = languageCode;
      // Guardar en localStorage para persistencia
      localStorage.setItem('hwa_language', languageCode);
    }
  }

  /**
   * Obtiene el idioma desde localStorage o detecta del navegador
   */
  private initializeLanguage(): void {
    const savedLanguage = localStorage.getItem('hwa_language');
    const supportedLanguages = this.supportedLanguages;
    
    if (savedLanguage && supportedLanguages[savedLanguage as keyof typeof supportedLanguages]) {
      this.currentLanguage = savedLanguage;
    } else {
      this.currentLanguage = this.detectBrowserLanguage();
      localStorage.setItem('hwa_language', this.currentLanguage);
    }
    
    // Update document attributes
    document.documentElement.lang = this.currentLanguage;
    document.documentElement.dir = this.isRTL() ? 'rtl' : 'ltr';
  }

  /**
   * Carga las traducciones para el idioma actual
   */
  async loadTranslations(languageCode?: string): Promise<void> {
    const lang = languageCode || this.currentLanguage;
    
    try {
      const response = await axios.get<TranslationResponse>(
        `${API_BASE_URL}/translations/${lang}`
      );
      
      this.translations = response.data.translations;
      this.currentLanguage = lang;
      this.isLoaded = true;
      
      // Actualizar el atributo dir del documento para RTL
      this.updateDocumentDirection();
      
    } catch (error) {
      console.error('Error loading translations:', error);
      
      // Si falla, intentar cargar inglés como fallback
      if (lang !== 'en') {
        await this.loadTranslations('en');
      }
    }
  }

  /**
   * Obtiene una traducción por clave
   */
  t(key: string, fallback?: string): string {
    if (!this.isLoaded) {
      return fallback || key;
    }
    
    return this.translations[key] || fallback || key;
  }

  /**
   * Obtiene todas las traducciones cargadas
   */
  getAllTranslations(): Record<string, string> {
    return { ...this.translations };
  }

  /**
   * Obtiene la lista de idiomas soportados
   */
  getSupportedLanguages(): Record<string, string> {
    return { ...this.supportedLanguages };
  }

  /**
   * Verifica si un idioma es RTL
   */
  isRTL(languageCode?: string): boolean {
    const lang = languageCode || this.currentLanguage;
    return this.rtlLanguages.includes(lang);
  }

  /**
   * Actualiza la dirección del documento para idiomas RTL
   */
  private updateDocumentDirection(): void {
    const isRTL = this.isRTL();
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', this.currentLanguage);
  }

  /**
   * Obtiene el nombre nativo del idioma actual
   */
  getCurrentLanguageName(): string {
    return this.supportedLanguages[this.currentLanguage as keyof typeof this.supportedLanguages] || 'English';
  }

  /**
   * Verifica si las traducciones están cargadas
   */
  isTranslationsLoaded(): boolean {
    return this.isLoaded;
  }

  /**
   * Cambia el idioma y recarga las traducciones
   */
  async changeLanguage(languageCode: string): Promise<void> {
    this.setCurrentLanguage(languageCode);
    await this.loadTranslations(languageCode);
  }
}

// Crear instancia singleton
const translationService = new TranslationService();

export default translationService;